import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatRupiah } from "@/lib/data";
import { api } from "@/lib/api";

const scrollToBooking = () => {
  const el = document.querySelector("#booking");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Packages = ({ onSelectPackage }) => {
  const [detail, setDetail] = useState(null);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const handleSelect = (pkg) => {
    if (onSelectPackage) onSelectPackage(pkg.name);
    scrollToBooking();
  };

  return (
    <section id="paket" className="py-24 bg-[#FAF6F0]" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
            Paket Dekorasi
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917] tracking-tight">
            Pilihan Paket Dekorasi
          </h2>
          <p className="mt-4 text-[#786F66]">
            Temukan konsep dekorasi yang sesuai dengan skala dan impian acara
            Anda.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group bg-white rounded-2xl overflow-hidden border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] hover:shadow-[0_12px_30px_rgba(197,160,89,0.15)] transition-all duration-300 flex flex-col"
              data-testid={`package-card-${pkg.id}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={pkg.photo}
                  alt={pkg.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/30 to-transparent" />
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-xl font-medium text-[#1C1917]">
                  {pkg.name}
                </h3>
                <p className="mt-1 text-sm text-[#786F66] leading-snug flex-1">
                  {pkg.tagline}
                </p>
                <div className="mt-4">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-[#967C52]">
                    Mulai Dari
                  </div>
                  <div className="font-serif text-2xl font-semibold text-[#C5A059]">
                    {formatRupiah(pkg.price)}
                  </div>
                </div>

                <div className="mt-5 flex gap-2">
                  <button
                    onClick={() => setDetail(pkg)}
                    data-testid={`package-detail-${pkg.id}-button`}
                    className="flex-1 px-4 py-2.5 rounded-full border border-[#E5D7C5] text-[11px] font-semibold tracking-widest uppercase text-[#4A443F] hover:border-[#C5A059] hover:text-[#C5A059] transition-all"
                  >
                    Lihat Detail
                  </button>
                  <button
                    onClick={() => handleSelect(pkg)}
                    data-testid={`package-select-${pkg.id}-button`}
                    className="px-4 py-2.5 rounded-full bg-[#1C1917] text-white hover:bg-[#C5A059] transition-all"
                    aria-label={`Pilih ${pkg.name}`}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-[#FFFDF9] border-[#E5D7C5]" data-testid="package-detail-dialog">
          {detail && (
            <>
              <div className="h-48 -mx-6 -mt-6 mb-2 overflow-hidden rounded-t-lg">
                <img src={detail.photo} alt={detail.name} className="w-full h-full object-cover" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#1C1917]">
                  {detail.name}
                </DialogTitle>
                <DialogDescription className="text-[#786F66]">
                  {detail.description}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-2 space-y-2">
                {(detail.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4A443F]">
                    <Check className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-[#E5D7C5] pt-4">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-[#967C52]">
                    Harga
                  </div>
                  <div className="font-serif text-2xl font-semibold text-[#C5A059]">
                    {formatRupiah(detail.price)}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleSelect(detail);
                    setDetail(null);
                  }}
                  data-testid="package-detail-select-button"
                  className="px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all"
                >
                  Pilih Paket Ini
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
